import type { Error } from 'api/__GENERATED';
import { schemas } from 'api/__GENERATED';
import type { AxiosResponse } from 'axios';
import { isAxiosError } from 'axios';
import * as R from 'remeda';
import type { Toast } from 'store';
import { z } from 'zod';

function errorToToast(error: Error): Toast {
  const toast: Toast = {
    id: '',
    message: error.message ?? '',
    severity: 'error',
    title: error.code ?? '',
  };

  return toast;
}

function zodErrorHandler(error: any, fallbackErrorToast: Toast) {
  // Check if zod Error
  const zodError =
    error instanceof z.ZodError
      ? error
      : error?.cause instanceof z.ZodError
        ? error.cause
        : null;

  if (zodError) {
    const toasts = zodError.issues.map(
      (issue: z.core.$ZodIssue): Toast => ({
        id: '',
        message: `${issue.path.join('.')}: ${issue.message}`,
        severity: 'error',
        title: 'Validation error',
      }),
    );
    throw toasts.length > 0 ? toasts : fallbackErrorToast;
  }
}
function apiErrorHandler(_errors: unknown, fallbackErrorToast: Toast) {
  const errors = z.array(schemas.Error).nullable().parse(_errors);
  if (!R.isDefined(errors)) throw fallbackErrorToast;
  if (errors.length === 0) throw fallbackErrorToast;
  throw errors.map(errorToToast);
}
type ApiBody<Data> = {
  hasSucceeded: boolean;
  errors: Error[] | null;
  data: Data;
};

// Zodios alias methods resolve to the parsed body. Raw `apiClient.axios.*`
// calls resolve to a full AxiosResponse wrapping the body. errorWrapper
// accepts either; onSuccess gets the optional response so callers that need
// headers (e.g. x-pagination) can read them.
function isAxiosResponse<Data>(
  value: ApiBody<Data> | AxiosResponse<ApiBody<Data>>,
): value is AxiosResponse<ApiBody<Data>> {
  return 'status' in value && 'headers' in value && 'data' in value;
}

async function errorWrapper<Data, Return>(args: {
  apiCall: Promise<ApiBody<Data> | AxiosResponse<ApiBody<Data>>>;
  onSuccess(data: Data, response?: AxiosResponse<ApiBody<Data>>): Return;
  error: Pick<Toast, 'message' | 'severity' | 'title'>;
}): Promise<Return> {
  const { apiCall, onSuccess, error } = args;

  const fallbackErrorToast: Toast = {
    id: '', // managed when adding in domain
    ...error,
  };

  return await apiCall.then(
    (result) => {
      const isAxios = isAxiosResponse(result);
      const response: AxiosResponse<ApiBody<Data>> | undefined = isAxios
        ? result
        : undefined;
      const body: ApiBody<Data> = isAxios ? result.data : result;
      const { data, hasSucceeded, errors } = body;
      if (hasSucceeded) return onSuccess(data, response);
      apiErrorHandler(errors, fallbackErrorToast);
      throw fallbackErrorToast;
    },
    (error) => {
      zodErrorHandler(error, fallbackErrorToast);

      if (!isAxiosError(error)) throw fallbackErrorToast;

      const responseData = error.response?.data;
      if (!R.isDefined(responseData)) throw fallbackErrorToast;
      apiErrorHandler(responseData.errors, fallbackErrorToast);
      throw fallbackErrorToast;
    },
  );
}
export { errorWrapper };
